import { DateInput, DateInputProps } from '@react-admin/ra-form-layout';
import { Configurable, usePreference } from 'react-admin';
import TzDatePreferencesEditor from './TzDatePreferencesEditor';
import {
  format,
  LOCAL_TIMEZONE,
  parse,
  SERVER_TIMEZONE,
  supportedDateFormats,
  TimePrecision,
} from './utils';
import { ReactElement } from 'react';

type TzDateInputImplProps = DateInputProps & {
  ignoreTimezoneInfo?: boolean;
  precision?: TimePrecision;
  serverTimezone?: string;
};

function TzDateInputImpl({
  format: userFormatFn,
  ignoreTimezoneInfo,
  parse: userParseFn,
  precision = 'day',
  serverTimezone = SERVER_TIMEZONE,
  ...dateInputProps
}: TzDateInputImplProps): ReactElement {
  const [dateFormat] = usePreference('format.date', supportedDateFormats[0].id);
  const [userTimezone] = usePreference('timezone', LOCAL_TIMEZONE);

  const formatFn = format({
    ignoreTimezoneInfo,
    serverTimezone,
    precision,
    userFormatFn,
    userTimezone,
  });
  const parseFn = parse({ ignoreTimezoneInfo, precision, serverTimezone, userParseFn });

  return <DateInput mask={dateFormat} {...dateInputProps} format={formatFn} parse={parseFn} />;
}

export type TzDateInputProps = TzDateInputImplProps & { preferenceKey?: string };

/**
 * Creates a timezone-aware DateInput component. This component wraps
 * React-Admin's `<DateInput />` component, but overloads the input's `format`
 * and `parse` functions to achieve any required timezone translations.
 *
 * This does mean that there are some caveats to the `format` and `parse` props
 * on this component to consider above those of the base React-Admin functions.
 *
 * On the `format` function, the value passed to the user-supplied `format`
 * function will be the in-memory form state as usual, but it should always
 * return an empty string (if the value is empty) or a fully-formatted ISO-8601
 * string. This string will be translated to the user's local timezone in the
 * form if `translateTimezone` is set.
 *
 * For `parse`, the value passed to the user-supplied `parse` function will be
 * an ISO-8601 datetime without timezone information, or null for no value. The
 * function should return the new form state value as usual. This date will be
 * translated to the server's timezone if `translateTimezone` is set.
 */
export default function TzDateInput({
  preferenceKey = 'tzDatePreferences',
  ...rest
}: TzDateInputProps): ReactElement {
  return (
    <Configurable
      editor={<TzDatePreferencesEditor showTimezone={false} />}
      preferenceKey={preferenceKey}
      sx={{ width: '100%' }}
    >
      <TzDateInputImpl {...rest} />
    </Configurable>
  );
}
